import React, { useEffect, useContext, useMemo } from 'react';
import classNames from 'classnames';
import {
  string,
  bool,
  shape,
  arrayOf,
  func
} from 'prop-types';
import { ExperienceContext, useConfigService, useStoreId } from '@thd-nucleus/experience-context';
import { Col } from '@thd-olt-component-react/core-ui';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { dataModel } from '../dataModel';
import { ProductSetContext } from '../context/ProductSetContext';
import { ProductPodContainer } from './ProductPodContainer/ProductPodContainer';
import { Summary } from './Summary/Summary';
import { EmptyContainer } from './EmptyContainer/EmptyContainer';
import {
  getFlatActiveProducts,
  mapPriceExistsInPackage,
  getProductGroupOrder,
  nullishCoalescer,
  buildNewFilledArray,
  buildLoadingState,
  hasPreviouslySelectedItem,
  createNewProductGroup
} from '../helper';
import './product-set.scss';

const ProductSet = ({
  isManagedPackage,
  packagePriorities,
  hideSummary,
  productGroups,
  updateProducts,
  loadingState,
  setLoadingState
}) => {
  const storeId = useStoreId();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const enableApplianceDeliveryCharge = useConfigService('enableApplianceDeliveryCharge') || false;
  const enableFreeDeliveryForExchange = useConfigService('enableFreeDeliveryForExchange') || false;
  const applianceDeliveryChargeValue = useConfigService('applianceDeliveryChargeValue') || 29;
  const {
    isCustomerIdentified,
    isExchangeCustomer
  } = useThdCustomer() || {};

  const isMobile = channel === 'mobile';

  useEffect(() => {
    setLoadingState(buildNewFilledArray(Object.keys(productGroups).length, false));
  }, [productGroups]);

  const orderedProductGroups = getProductGroupOrder(productGroups, packagePriorities);

  const toggleActiveProduct = (productGroup, products, newProductId) => {
    const updatedActiveArray = products.map((product) => {
      return {
        ...product,
        active: product.itemId === newProductId
      };
    });
    setLoadingState(buildLoadingState(loadingState, orderedProductGroups, productGroup));
    updateProducts({
      ...productGroups,
      [productGroup]: updatedActiveArray
    });
  };

  const swapGroup = (swappedProducts, productGroup) => {
    const currentProducts = productGroups[productGroup];
    const newSwappedProducts = swappedProducts.map((product, index) => {
      return {
        ...product,
        info: {
          ...product.info,
          productGroup
        },
        active: nullishCoalescer(product?.active, index === 0),
        removed: nullishCoalescer(product?.removed, false),
        swappedProducts: currentProducts
      };
    });
    setLoadingState(buildLoadingState(loadingState, orderedProductGroups, productGroup));
    updateProducts({
      ...productGroups,
      [productGroup]: newSwappedProducts
    });
  };

  const removeGroup = (productGroup) => {
    const removedGroup = productGroups[productGroup];
    const newRemovedGroup = removedGroup.map((product) => {
      return {
        ...product,
        removed: true
      };
    });
    setLoadingState(buildLoadingState(loadingState, orderedProductGroups, productGroup));
    updateProducts({
      ...productGroups,
      [productGroup]: newRemovedGroup
    });
  };

  const addNewGroup = (removedGroup, newProducts, productGroup) => {
    setLoadingState(buildLoadingState(loadingState, orderedProductGroups, productGroup));
    if (hasPreviouslySelectedItem(removedGroup, newProducts)) {
      updateProducts({
        ...productGroups,
        [productGroup]: createNewProductGroup(newProducts, productGroup, removedGroup)
      });
    } else {
      updateProducts({
        ...productGroups,
        [productGroup]: createNewProductGroup(newProducts, productGroup)
      });
    }
  };

  const productSetClasses = classNames('product-set', {
    'product-set--extra-scroll': isConsumerApp,
    'product-set--extra-scroll-with-map': isConsumerApp && mapPriceExistsInPackage(getFlatActiveProducts(productGroups))
  });

  return (
    <Col className={productSetClasses} data-testid="product-set" data-component="ProductSet">
      <ProductSetContext.Provider value={useMemo(() => (
        {
          toggleActiveProduct,
          swapGroup,
          removeGroup,
          addNewGroup,
          isMobile,
          storeId,
          isCustomerIdentified,
          isExchangeCustomer
        }
      ), [
        toggleActiveProduct,
        swapGroup,
        removeGroup,
        addNewGroup,
        isMobile,
        storeId,
        isCustomerIdentified,
        isExchangeCustomer
      ])}
      >
        {productGroups && (
          <>
            {isManagedPackage && (
              <div className="product-set__title">
                Manage Package
              </div>
            )}

            {orderedProductGroups.map((key, index) => {
              return !productGroups[key].some((product) => product.removed) ? (
                <div key={key}>
                  <ProductPodContainer
                    key={key}
                    position={index}
                    products={productGroups[key]}
                    productGroup={key}
                    showCTA={isManagedPackage}
                    isLoading={loadingState[index]}
                  />
                  <hr />
                </div>
              ) : (
                <EmptyContainer
                  products={productGroups[key]}
                  key={key}
                  isLoading={loadingState[index]}
                />
              );
            })}

            {(!!Object.keys(productGroups).length && !hideSummary) && (
              <Summary
                enableApplianceDeliveryCharge={enableApplianceDeliveryCharge}
                enableFreeDeliveryForExchange={enableFreeDeliveryForExchange}
                applianceDeliveryChargeValue={applianceDeliveryChargeValue}
                productGroups={productGroups}
                isLoading={loadingState.some((loading) => loading)}
                isMobile={isMobile}
              />
            )}
          </>
        )}
      </ProductSetContext.Provider>
    </Col>
  );
};

ProductSet.propTypes = {
  isManagedPackage: bool,
  packagePriorities: arrayOf(string),
  hideSummary: bool,
  productGroups: shape({}),
  updateProducts: func,
  loadingState: arrayOf(bool),
  setLoadingState: func
};

ProductSet.defaultProps = {
  isManagedPackage: false,
  packagePriorities: [],
  hideSummary: false,
  productGroups: {},
  updateProducts: () => {},
  loadingState: [],
  setLoadingState: () => {}
};

ProductSet.displayName = 'ProductSet';

ProductSet.dataModel = dataModel;

export { ProductSet };
